import { Gear } from "@phosphor-icons/react";
import { toFlattenedObject } from "@validereinc/utilities";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Accordion } from "../Accordion";
import Button from "../Button/Button";
import { Drawer } from "../Drawer";
import Form from "../Form/Form";
import useForm from "../utils/hooks/useForm";
import { expandObject } from "../utils/objectFormatter";
import SaveFilterModal from "./SaveFilterModal";
import {
  clearFiltersButtonText,
  saveFiltersButtonText,
  title,
} from "./filterDrawerConstants";
import { getFilterObject } from "./filterDrawerHelpers";
import type { FilterDrawerProps, FilterValue, SectionType } from "./types";

const AccordionPanel = Accordion.AccordionPanel;

/**
 * A re-usable Drawer component to allow users to apply filters.
 *
 * @deprecated this is not a valid shared React component. Use the new filtering patterns in the appropriate app you are building.
 */
const FilterDrawer = ({
  isOpen,
  onClose,
  onChange,
  onReset: onResetCallback,
  value = {},
  filters,
  defaultActiveKeys,
  onSaveFilter,
  onIconClick,
}: FilterDrawerProps) => {
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [resetToggle, setResetToggle] = useState(false);
  const isMounted = useRef(false); // needed to avoid reset useEffect on first render

  const sections = useMemo(
    () =>
      (filters || []).reduce((total: SectionType[], { section, component }) => {
        const sectionIndex = total.findIndex(({ title }) => title === section);

        if (sectionIndex > -1) {
          total[sectionIndex].fields.push(component);
        } else if (section) {
          total.push({ title: section, fields: [component] });
        } else {
          total.push({ title: "Filters", fields: [component] });
        }

        return total;
      }, []),
    [filters]
  );
  const form = useForm({ defaultValues: value });

  const onReset = () => {
    const newValues = toFlattenedObject(form.getValues(), {
      transformObjectValue: (val) => val?.id,
    });

    const newFormValues = Object.fromEntries(
      Object.entries(newValues).map(([key]) => {
        // NOTE: we need to reset to "" because undefined is not a valid input type value
        return [key, ""];
      })
    );

    const returnValue = expandObject(newFormValues);

    if (onResetCallback) {
      onResetCallback(returnValue);
    }
    setResetToggle((prevState) => !prevState);
  };

  useEffect(() => {
    // skip reset on first useEffect run
    if (isMounted.current) {
      const defaultResetValues = filters.reduce<Record<string, string>>(
        (filter, currFilter) => {
          const props = currFilter.component.props;

          if (props?.name) {
            filter[props.name] = "";
          } else if (props?.attribute?.field_name) {
            filter[`custom_attributes.${props.attribute.field_name}`] = "";
          }
          return filter;
        },
        {}
      );

      form.reset(defaultResetValues);
    } else {
      isMounted.current = true;
    }
  }, [resetToggle]);

  const onSubmit = (values: FilterValue) => {
    onChange(values);
    onClose();
  };

  const handleSubmit = () => {
    form.handleSubmit(onSubmit)();
  };

  const handleSaveFilter = async (name: string) => {
    return form.handleSubmit(async (values: FilterValue) => {
      const { _columnsToShow, _savedFilter, ...filterValues } = values;

      await onSaveFilter?.(name, getFilterObject(filterValues));
      onSubmit(values);
    })();
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      iconActionRight={
        onIconClick ? (
          <Gear
            size={20}
            onClick={() => onIconClick()}
          />
        ) : undefined
      }
      titleAction={
        onSaveFilter ? (
          <Button
            variant="outline"
            onClick={() => setShowSaveModal(true)}
          >
            Save Filter
          </Button>
        ) : undefined
      }
      actionRow={[
        <Button
          key="clear-filters-action"
          onClick={onReset}
        >
          {clearFiltersButtonText}
        </Button>,
        <Button
          key="apply-filters-action"
          onClick={handleSubmit}
          variant="primary"
        >
          {saveFiltersButtonText}
        </Button>,
      ]}
    >
      <Form
        {...form}
        onSubmit={onSubmit}
      >
        <Accordion defaultActiveKeys={defaultActiveKeys}>
          {sections.map(({ title, fields }, index) => {
            return (
              <AccordionPanel
                title={title}
                key={`unique-${title}-${index}`}
                dataKey={title}
              >
                <>{fields?.map((field) => field)}</>
              </AccordionPanel>
            );
          })}
        </Accordion>
      </Form>
      {onSaveFilter ? (
        <SaveFilterModal
          isOpen={showSaveModal}
          onClose={() => setShowSaveModal(false)}
          onSave={handleSubmit}
          onSaveFilter={handleSaveFilter}
        />
      ) : undefined}
    </Drawer>
  );
};

export default FilterDrawer;
